import { canonicalUrl, extensionUrl } from "@repo/fhir-systems";

export {
  ACTIVITY_DEFINITION_CATALOG_SYSTEM,
  AI_DEVICE_TYPE_SYSTEM,
  CURATION_SESSION_SYSTEM,
  HS_PROTOCOL_TYPE_SYSTEM,
  MESSAGE_CHANNEL_SYSTEM,
  OUTBOUND_MESSAGE_PURPOSE_SYSTEM,
  PLAN_DEFINITION_KIND_URL,
  PROCEDURE_CODE_SYSTEM,
  SCHEDULE_SERVICE_TYPE_SYSTEM,
  SCHEDULING_PROFILE_TYPE_SYSTEM,
  TASK_STATUS_REASON_SYSTEM,
  VISIT_KIND_SYSTEM,
} from "@repo/fhir-systems/code-systems";
export {
  ACTION_PARAMETER_VALUE_URL,
  ACTIVITY_DEFINITION_IDS_URL,
  ACTIVITY_PARAMETER_URL,
  CARE_PLAN_GOALS_URL,
  ENCOUNTER_CLASS_URL,
  NOTE_SCHEMA_URL,
  OBSERVATION_SCHEMA_URL,
  PERFORMER_TYPE_URL,
  PROCEDURE_NOTE_CONFIG_URL,
  PROTOCOL_MEMBER_ID_URL,
  PROTOCOL_SCHEDULING_URL,
  REQUIRED_RESOURCE_URL,
  SOAP_HINT_URL,
  SUPPORTS_CONDITIONS_URL,
  TASK_PARAMETER_VALUE_URL,
  TIME_WINDOW_URL,
  TREATMENT_CATEGORY_URL,
} from "@repo/fhir-systems/extensions";

import { SCHEDULE_SERVICE_TYPE_SYSTEM, TASK_STATUS_REASON_SYSTEM } from "@repo/fhir-systems/code-systems";

// ── ActivityDefinition catalog ──

export { FHIR_CANONICAL_BASE } from "@repo/fhir-systems";
export const ORDER_DEFINITION_PROVENANCE_URL = extensionUrl("order-definition-provenance");
export const ORDER_DIAGNOSTIC_DEFINITION_SNAPSHOT_URL = extensionUrl("order-diagnostic-definition-snapshot");
export const ORDER_PROCEDURE_DEFINITION_SNAPSHOT_URL = extensionUrl("order-procedure-definition-snapshot");
// TODO(ad-compliance): derive from authenticated project's Organization resource
export const DEFAULT_PUBLISHER = "MindForge";
export { SCHEDULING_PARAMS_URL } from "@repo/fhir-systems";

export const SCHEDULING_PROFILE_TYPE_CODE = "duration";
export { TIMEZONE_EXTENSION_URL } from "@repo/fhir-systems";
// Practitioner.identifier system carrying the clinician's Google Workspace login (MIN-281).
export { GOOGLE_WORKSPACE_EMAIL_SYSTEM } from "@repo/fhir-systems/identifiers";

/** Stable definition-owned member identity for Protocol scheduling facts nested beneath a Catalog Definition action. */

/** Medplum task-elements extension — drives Task.owner / Task.performerType during PlanDefinition/$apply. */
export { TASK_ELEMENTS_URL } from "@repo/fhir-systems";

export type ActivityDefinitionCatalog = "diagnostics" | "treatments";
/**
 * Both catalogs mint `ServiceRequest`-kind ActivityDefinitions, and nothing
 * else can: catalog authoring reads the kind from HERE rather than from input.
 *
 * That is load-bearing downstream. A curated plan materializes into OrderSets,
 * which hold requested procedures and nothing else — so a leaf whose kind is
 * not ServiceRequest has no home. Adding a catalog with another kind (a
 * questionnaire, say) means deciding where its leaves live BEFORE adding the
 * entry, not after.
 */
export const CATALOG_KIND_MAP: Record<ActivityDefinitionCatalog, string> = {
  diagnostics: "ServiceRequest",
  treatments: "ServiceRequest",
};
export const CATALOG_DISPLAY_MAP: Record<ActivityDefinitionCatalog, string> = {
  diagnostics: "Diagnostics",
  treatments: "Treatments",
};

// ── AI agent ──

export const AI_DEVICE_TYPE_CODE = "ai-agent";
export const AI_DEVICE_DISPLAY = "MF Agent";
export const AI_DEVICE_TYPE_DISPLAY = "AI Agent";
/** Stable identity shared by the canonical seed and the independently runnable AI Device bot seed. */
export const AI_DEVICE_DOMAIN_ID = "01900000-0000-7004-8000-000000000021";

// ── Patient intake ──
/** The seeded intake form's lineage. Not its name: a name is a correctable label. */
export const INTAKE_QUESTIONNAIRE_URL = canonicalUrl("Questionnaire", "patient-intake");
/**
 * Origin for patient-facing links we email out (the portal welcomes and the visit
 * reminder). Paths live in `lib/patient-portal-links.ts`.
 *
 * The fallback is the patient portal's local dev port — 3003, per the pinned
 * ports in CLAUDE.md — and is *only* ever correct locally. Every deployed
 * environment injects the real hostname as a task-definition env var
 * (`infrastructure/terraform/services/api`), because a wrong value here does not
 * fail: it silently mails an unreachable link to a real patient.
 */
export const PATIENT_PORTAL_BASE_URL =
  // biome-ignore lint/complexity/useLiteralKeys: env access
  globalThis.process?.env?.["PATIENT_PORTAL_BASE_URL"] ?? "http://localhost:3003";

// ── Trusted external identifier systems ──
// Identifier namespace used by the GHL integration.
export { GHL_CONTACT_ID_SYSTEM } from "@repo/fhir-systems";

// ── Outbound messages ──
/** Communication.category coding system carrying the OutboundMessage purpose — also the
 *  partition key separating outbound messages from notifications (ADR-0001). */

/** Communication.medium coding system for the delivery channel. We own this set: FHIR's
 *  example binding (v3-ParticipationMode) has no SMS code, so an owned system keeps
 *  email/SMS discrimination uniform. */

/**
 * `ServiceRequest.requisition` system identifying the curated session instance a
 * request belongs to. The value is the curation entry's opaque `sessionId`, so
 * every request from one curated protocol occurrence shares a requisition — the
 * bookable unit (one requisition = one session instance = one Appointment).
 */

/** Per-entry extension on a curation PD action: ISO instant the entry was added (display/sort only). */

/**
 * `Appointment.appointmentType` system carrying the visit's KIND — how its work
 * came to be, the one fact about a visit that nothing else can answer.
 *
 * A stored marker rather than an inference. Kind used to be read back from
 * which of three optional fields happened to be present, which cannot survive
 * reschedule: a rescheduled standalone visit re-books the set it already had
 * and is then indistinguishable from a curated session.
 *
 * `appointmentType` and not an extension, because that is the field FHIR
 * indexes — visits become searchable by kind, which they were not.
 */

// ── Performer type ──

export type PerformerType = "clinician" | "assistant";

// ── PlanDefinition kind ──
//
// Protocol, Program and Curation all store as PlanDefinition, so `type` is what
// tells them apart — and it is a KIND MARKER, not a field anyone edits. Every
// PlanDefinition this system writes carries exactly one of these codings, which
// is what lets a search say "protocols only" and a load refuse a row of the
// wrong kind.
//
// This used to be four systems for the one idea: `plan-definition-type` on
// seeded protocols, `program-type` on seeded programs, this constant with no
// writer at all, and a `.com` misspelling of `program-type` hardcoded in a
// data-store test that therefore proved nothing about real data. Curations had
// no marker whatsoever. One system, one code per kind, one writer each.

export const PLAN_DEFINITION_KIND = {
  flow: "flow",
  program: "program",
  protocol: "protocol",
} as const;
export type PlanDefinitionKind = (typeof PLAN_DEFINITION_KIND)[keyof typeof PLAN_DEFINITION_KIND];

// ── Protocol ──

// HealthcareService → PlanDefinition backlink (the HS's owning protocol).
// Default booking-grid cadence stamped on a protocol HS when the caller omits one.
// Matches the seed default so builder-created protocols behave identically to seeded ones.
export const DEFAULT_PROTOCOL_ALIGNMENT_INTERVAL = { value: 15, unit: "min" as const };
// HS type[].coding system for the protocol identifier code (mirrors to Schedule.serviceType[].coding).

// PlanDefinition → HealthcareService forward link (the protocol's bookable-service).

// ── Scheduling ──
export { DEFAULT_TIMEZONE } from "@repo/domain";
export const DEFAULT_AVAILABILITY_SEARCH_DAYS = 30;
export const DEFAULT_AVAILABILITY_WINDOW_COUNT = 100;
export const MS_PER_MINUTE = 60_000;
export const MS_PER_DAY = 86_400_000;
export const MINUTES_PER_HOUR = 60;
export const STALE_APPOINTMENT_GRACE_PERIOD_MIN = 30;
export const RECURRING_PATTERN_URL = extensionUrl("RecurringPattern");
export const AUTO_COMPLETED_EXTENSION_URL = extensionUrl("AutoCompleted");

export const SCHEDULE_SERVICE_TYPE_CODE = "generic";
export const SCHEDULE_SERVICE_TYPE_TOKEN = `${SCHEDULE_SERVICE_TYPE_SYSTEM}|${SCHEDULE_SERVICE_TYPE_CODE}`;
export const SCHEDULE_SERVICE_TYPE_CODING = {
  system: SCHEDULE_SERVICE_TYPE_SYSTEM,
  code: SCHEDULE_SERVICE_TYPE_CODE,
} as const;
// Medplum 5.1.11 $find/$book: serviceType on input Slot must carry this extension
// pointing at the HealthcareService backing the scheduling request.
export { SERVICE_TYPE_REFERENCE_URL } from "@repo/fhir-systems";

// ── Task / Procedure ──

// Wire format owned by @repo/mentions — re-exported here so existing constants
// imports keep working. The extension URL is declared exactly once, there.
export { ANNOTATION_RECIPIENT_URL } from "@repo/mentions";

// Task.statusReason coding system — distinguishes why a Task entered a terminal status.

export const TASK_STATUS_REASON_USER_CANCELLED = {
  coding: [{ system: TASK_STATUS_REASON_SYSTEM, code: "visit-cancelled", display: "Visit cancelled" }],
};
export const TASK_STATUS_REASON_SUPERSEDED = {
  coding: [{ system: TASK_STATUS_REASON_SYSTEM, code: "superseded", display: "Replaced by rescheduled visit" }],
};

// ── Time window ──

export const TIME_WINDOWS = ["early_morning", "mid_morning", "midday", "early_afternoon", "late_afternoon"] as const;
export type TimeWindow = (typeof TIME_WINDOWS)[number];

export interface TimeWindowPreset {
  label: string;
  shortLabel: string;
  startHour: number;
  endHour: number;
  midpointTime: string;
}

export const TIME_WINDOW_PRESETS: Record<TimeWindow, TimeWindowPreset> = {
  early_morning: {
    label: "Early Morning (7–9 AM)",
    shortLabel: "Early AM",
    startHour: 7,
    endHour: 9,
    midpointTime: "08:00",
  },
  mid_morning: {
    label: "Mid Morning (9–11 AM)",
    shortLabel: "Mid AM",
    startHour: 9,
    endHour: 11,
    midpointTime: "10:00",
  },
  midday: { label: "Midday (11 AM–1 PM)", shortLabel: "Midday", startHour: 11, endHour: 13, midpointTime: "12:00" },
  early_afternoon: {
    label: "Early Afternoon (1–3 PM)",
    shortLabel: "Early PM",
    startHour: 13,
    endHour: 15,
    midpointTime: "14:00",
  },
  late_afternoon: {
    label: "Late Afternoon (3–5 PM)",
    shortLabel: "Late PM",
    startHour: 15,
    endHour: 17,
    midpointTime: "16:00",
  },
};

/** Owning step member id, scoped by the Visit's OrderSet reference. */
export const VISIT_ASSIGNMENT_STEP_ID_URL = extensionUrl("visit-assignment-step-id");

export { VISIT_NOTE_KEY_SYSTEM } from "@repo/fhir-systems/identifiers";
